import { Box } from "@mui/material";
import { Icon, Typography } from "@trimble/modus-cmx";
import type { ComponentProps, ReactNode } from "react";
import { NavLink } from "react-router";
import TimeAgo from "@/components/app/Navigation/GlobalHeader/Notifications/TimeAgo";
import { recentlyViewedListItemButtonSx } from "./recentlyViewedListItemSx";
import type { RecentlyViewedItem } from "./types";

const iconByType: Record<
  RecentlyViewedItem["type"],
  ComponentProps<typeof Icon>["name"]
> = {
  contract: "document",
  amendment: "document",
  customer: "building_corporate",
  device: "in_cab_device",
};

type RecentlyViewedListItemProps = {
  item: RecentlyViewedItem;
  onSelect: () => void;
};

function EntityIcon({ name }: { name: ComponentProps<typeof Icon>["name"] }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: 16,
        height: 16,
        flexShrink: 0,
        mt: "2px",
        color: "var(--modus-wc-color-gray-6)",
      }}
    >
      <Icon name={name} variant="solid" size="xs" decorative />
    </Box>
  );
}

function RecentlyViewedText({ children }: { children: ReactNode }) {
  return (
    <Typography
      hierarchy="p"
      size="sm"
      style={{
        color: "var(--modus-wc-color-gray-6)",
        overflowWrap: "anywhere",
      }}
    >
      <span>{children}</span>
    </Typography>
  );
}

function Timestamp({ visitedAt }: { visitedAt: string }) {
  return (
    <RecentlyViewedText>
      <TimeAgo dateTime={visitedAt} />
    </RecentlyViewedText>
  );
}

const getDeviceSerialNumber = (item: RecentlyViewedItem) => {
  if (item.type !== "device") {
    return "";
  }
  return item.serialNumber?.trim() ?? "";
};

const getLinkRecentlyViewedLink = (item: RecentlyViewedItem) => {
  if (item.type !== "device") {
    return undefined;
  }

  const serialNumber = getDeviceSerialNumber(item);
  return {
    resetDeviceTab: true,
    dismissDialogs: true,
    ...(serialNumber.length > 0 ? { deviceSearch: serialNumber } : {}),
  };
};

const getRecentlyViewedLinkTo = (item: RecentlyViewedItem) => {
  const serialNumber = getDeviceSerialNumber(item);
  if (serialNumber.length === 0) {
    return item.path;
  }
  return `${item.path}?search=${encodeURIComponent(serialNumber)}`;
};

function RecentlyViewedListItem({
  item,
  onSelect,
}: RecentlyViewedListItemProps) {
  const isCustomer = item.type === "customer";

  return (
    <Box
      component={NavLink}
      to={getRecentlyViewedLinkTo(item)}
      state={getLinkRecentlyViewedLink(item)}
      onClick={onSelect}
      sx={recentlyViewedListItemButtonSx}
    >
      <EntityIcon name={iconByType[item.type]} />
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "4px",
          minWidth: 0,
        }}
      >
        <Typography
          hierarchy="p"
          size="md"
          style={{
            color: "var(--modus-wc-color-trimble-blue)",
            overflowWrap: "anywhere",
          }}
        >
          <span>{item.title}</span>
        </Typography>
        {!isCustomer && item.subtitle ? (
          <RecentlyViewedText>{item.subtitle}</RecentlyViewedText>
        ) : null}
        <Box style={{ marginTop: "6px" }}>
          <Timestamp visitedAt={item.visitedAt} />
        </Box>
      </Box>
    </Box>
  );
}

export default RecentlyViewedListItem;
